import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { parse, isValid, differenceInYears } from "date-fns";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { verifySchufa } from "@/api";
import { handleIncompleteRegistration } from "@/lib/handleIncompleteRegistration";
import StepHeader from "@/components/StepHeader";
import ActionRow from "@/components/ActionRow";
import ErrorModal from "@/components/ErrorModal";
import { extractApiError } from "@/lib/extractApiError";

const schufaSchema = z.object({
  firstName: z.string().min(1, "Bitte gib deinen Vornamen ein."),
  lastName: z.string().min(1, "Bitte gib deinen Nachnamen ein."),
  birthDate: z
    .string()
    .min(1, "Bitte gib dein Geburtsdatum ein.")
    .regex(
      /^\d{2}\/\d{2}\/\d{4}$/,
      "Bitte im Format TT/MM/JJJJ eingeben."
    )
    .refine(
      (val) => {
        const parsed = parse(val, "dd/MM/yyyy", new Date());
        return isValid(parsed);
      },
      "Bitte gib ein gültiges Datum ein."
    )
    .refine(
      (val) => {
        const parsed = parse(val, "dd/MM/yyyy", new Date());
        if (!isValid(parsed)) return false;
        const age = differenceInYears(new Date(), parsed);
        return age >= 0 && age <= 120;
      },
      "Das Geburtsdatum muss innerhalb der letzten 120 Jahre liegen."
    ),
  streetName: z.string().min(1, "Bitte gib deine Straße ein."),
  buildingNumber: z.string().min(1, "Bitte gib deine Hausnummer ein."),
  buildingAdditional: z.string().optional(),
  zipCode: z
    .string()
    .min(1, "Bitte gib deine Postleitzahl ein.")
    .regex(/^\d{5}$/, "Bitte gib eine gültige deutsche Postleitzahl ein."),
  city: z.string().min(1, "Bitte gib deine Stadt ein."),
});

const SchufaPage = ({ skipGuard = false }) => {
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(false);
  const [apiError, setApiError] = useState(null);

  const registerData = sessionStorage.getItem("registerData");

  // Page guard
  useEffect(() => {
    if (!skipGuard && !registerData) navigate("/register");
  }, [skipGuard, registerData, navigate]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(schufaSchema),
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      birthDate: "",
      streetName: "",
      buildingNumber: "",
      buildingAdditional: "",
      zipCode: "",
      city: "",
    },
  });

  const handleBirthDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 8) value = value.slice(0, 8);
    if (value.length >= 5) {
      value = value.slice(0, 2) + "/" + value.slice(2, 4) + "/" + value.slice(4);
    } else if (value.length >= 3) {
      value = value.slice(0, 2) + "/" + value.slice(2);
    }
    setValue("birthDate", value, { shouldValidate: true });
  };

  const onSubmit = async (data) => {
    setApiError(null);
    setIsVerifying(true);

    const parsed = JSON.parse(registerData);
    const token = parsed.token.uuid;

    const [dd, mm, yyyy] = data.birthDate.split("/");

    try {
      await verifySchufa(
        {
          consumer: {
            ...data,
            birthDate: `${yyyy}-${mm}-${dd}`,
            country: "DEU",
          },
        },
        token
      );
      // Success — schufa verified and registration fully complete (rare)
      sessionStorage.removeItem("registerData");
    } catch (error) {
      setIsVerifying(false);
      const errData = error.response?.data;
      if (errData?.data?.error === "error_registration_incomplete") {
        handleIncompleteRegistration(errData.data.details, navigate);
      } else {
        reset();
        setApiError(extractApiError(errData));
      }
    }
  };

  if (!skipGuard && !registerData) return null;

  if (isVerifying) {
    return (
      <div className="flex flex-col gap-10 items-center w-full max-w-[720px] pt-[80px] pb-[48px] px-5">
        <StepHeader stepTitle="Altersverifizierung" step={2} migration={sessionStorage.getItem("migration") === "true"} />
        <div className="flex flex-col items-center gap-6 py-20">
          <Loader2 className="w-10 h-10 animate-spin text-black" />
          <p className="text-black text-center text-[18px] font-medium">
            Bitte warte...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        className="absolute top-3 left-3 z-10 md:h-[54px] md:px-[20px] md:text-[24px] md:rounded-[14px]"
        onClick={() => { sessionStorage.removeItem("migration"); navigate("/"); }}
      >
        Anmelden
      </Button>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-10 items-center w-full max-w-[720px] pt-[80px] pb-[48px] px-5"
      >
        <div className="flex flex-col gap-4 items-center w-full">
          <StepHeader stepTitle="Altersverifizierung" step={2} migration={sessionStorage.getItem("migration") === "true"} />
          <p className="text-black text-center text-[14px] font-medium leading-[1.4]">
            Zur Altersverifizierung führen wir mittels eines Drittanbieters, der
            Schufa Holding AG, Kormoranweg 5, 65201 Wiesbaden (SCHUFA), eine
            Adressverifikation bzw. Identitätsprüfung durch. Dies dient
            lediglich der Überprüfung Ihrer Volljährigkeit.
          </p>
          <p className="text-black text-center text-[14px] font-medium leading-[1.4]">
            Es findet keine Bonitätsprüfung statt und es werden auch keine
            bonitätsbezogenen Daten übermittelt. Nähere Informationen erhalten du
            in den{" "}
            <a href="/legal/datenschutz" className="underline">
              Datenschutzbestimmungen
            </a>
            .
          </p>
        </div>

        <div className="flex flex-col gap-6 items-center w-full max-w-[480px]">
          <div className="flex flex-col lg:flex-row gap-6 w-full">
            <div className="w-full">
              <Input
                label="Vorname"
                placeholder="Dein Vorname"
                {...register("firstName")}
              />
              {errors.firstName && (
                <p className="text-primary text-[13px] font-medium mt-1">
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div className="w-full">
              <Input
                label="Nachname"
                placeholder="Dein Nachname"
                {...register("lastName")}
              />
              {errors.lastName && (
                <p className="text-primary text-[13px] font-medium mt-1">
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>
          <div className="w-full">
            <Input
              label="Geburtsdatum"
              placeholder="TT/MM/JJJJ"
              maxLength={10}
              {...register("birthDate")}
              onChange={handleBirthDateChange}
            />
            {errors.birthDate && (
              <p className="text-primary text-[13px] font-medium mt-1">
                {errors.birthDate.message}
              </p>
            )}
          </div>
          <div className="flex flex-col lg:flex-row gap-6 w-full">
            <div className="w-full">
              <Input
                label="Straße"
                placeholder="Straße eingeben"
                {...register("streetName")}
              />
              {errors.streetName && (
                <p className="text-primary text-[13px] font-medium mt-1">
                  {errors.streetName.message}
                </p>
              )}
            </div>
            <div className="w-full lg:w-45 lg:shrink-0">
              <Input
                label="Hausnummer"
                placeholder="Hausnummer eingeben"
                {...register("buildingNumber")}
              />
              {errors.buildingNumber && (
                <p className="text-primary text-[13px] font-medium mt-1">
                  {errors.buildingNumber.message}
                </p>
              )}
            </div>
          </div>
          <div className="w-full">
            <Input
              label="Adresszusatz (z. B. Etage)"
              placeholder="Zusätzliche Informationen eingeben"
              {...register("buildingAdditional")}
            />
          </div>

          <div className="flex gap-6 w-full">
            <div className="w-[140px] shrink-0">
              <Input
                label="Postleitzahl"
                placeholder="XXXXX"
                maxLength={5}
                inputMode="numeric"
                {...register("zipCode")}
              />
              {errors.zipCode && (
                <p className="text-primary text-[13px] font-medium mt-1">
                  {errors.zipCode.message}
                </p>
              )}
            </div>
            <div className="flex-1">
              <Input
                label="Stadt"
                placeholder="Stadt eingeben"
                {...register("city")}
              />
              {errors.city && (
                <p className="text-primary text-[13px] font-medium mt-1">
                  {errors.city.message}
                </p>
              )}
            </div>
          </div>

          <ErrorModal
            open={!!apiError}
            onClose={() => setApiError(null)}
            message={apiError?.message}
            errorCode={apiError?.errorCode}
          />

          <ActionRow label="Daten verifizieren" disabled={!isValid} />
        </div>
      </form>
    </>
  );
};

export default SchufaPage;
